import React from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {compose} from 'react-recompose';
import {noEmpty} from '@packages/utilities/component';
import {
  styledDiv,
  styled,
  Z_INDEXES,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {Clickable} from '@packages/systems/design-system/logic/clickable';
import {lighten} from '@packages/systems/core/utils/ColorUtils';
import {PencilMedium as Pencil} from '@packages/systems/ui/icons';
import OneLiner from '@packages/systems/design-system/OneLiner';
import {FlexRow} from '@packages/systems/design-system/Layout';
import {emptyArray} from '@packages/utilities/fp/utils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';

// @ts-expect-error - TS7031 - Binding element 'control' implicitly has an 'any' type. | TS7031 - Binding element 'light' implicitly has an 'any' type. | TS7031 - Binding element 'selected' implicitly has an 'any' type. | TS7031 - Binding element 'colors' implicitly has an 'any' type.
const getBorderStyle = ({control, light, selected, colors}) => {
  if (light) {
    return {
      border: `${control.borderWidth}px solid ${lighten(8)(colors.borderDeep)}`,
    };
  }

  if (selected) {
    return {
      border: `${control.borderWidth}px solid ${colors.accent}`,
      borderRadius: control.borderRadius,
      zIndex: Z_INDEXES.zIndexMax,
    };
  }

  return {border: `${control.borderWidth}px solid ${colors.borderDeep}`};
};

export const Wrapper = styled(Clickable)(
  ({
    theme: {colors, gutters, control},
    style,
    height,
    light,
    condensed,
    selected,
  }) => ({
    display: 'flex',
    position: 'relative',
    boxSizing: 'border-box',
    height:
      height || (condensed ? control.height : control.height + gutters.narrow),
    paddingRight: gutters.narrow,
    paddingLeft: gutters.narrow,
    background: light
      ? lighten(4.5)(colors.backgroundDeepAlt)
      : colors.backgroundDeepAlt,
    ...getBorderStyle({control, light, selected, colors}),
    ...style,
  }),
  {
    first: ({theme: {control}}) => ({
      borderTopRightRadius: control.borderRadius,
      borderTopLeftRadius: control.borderRadius,
    }),
    last: ({theme: {control}}) => ({
      borderBottomRightRadius: control.borderRadius,
      borderBottomLeftRadius: control.borderRadius,
    }),
  }
);

const ContentWrapper = emotionStyled('div')({
  color: tokens.colors.textInverse,
});
const PencilIcon = styled(Pencil)(
  ({theme}) => ({
    height: 12,
    width: 12,
    color: theme.colors.foregroundDimmer,
  }),
  {
    hover: ({theme}) => ({
      color: theme.colors.foregroundVivid,
    }),
  }
);

export const Label = ({title}: {title: string}) => (
  <OneLiner>
    <FlexRow align="center">{title}</FlexRow>
  </OneLiner>
);
export const RadioIcon = styledDiv(({theme: {colors, gutters}, selected}) => {
  const borderWidth = selected ? 3 : 1;
  return {
    height: gutters.x12,
    width: gutters.x12,
    border: `${borderWidth}px solid ${colors.borderDeep}`,
    marginRight: gutters.x8,
    borderRadius: '100%',
    backgroundColor: selected ? colors.foreground : colors.backgroundDeep,
  };
});

const NoEmpty = compose(noEmpty)(FlexRow);

// Every prop is optional — `ListHeader` is composed via `styled(ListHeader)`
// in `ListItem` below, and call sites (here in `ErrorItem`, and across
// designer-flux) supply props piecemeal. The values stay `any` because each
// prop is forwarded verbatim to `Wrapper`/`FlexRow`/etc. whose own types
// already constrain it.
type ListHeaderProps = {
  [K in
    | 'icon'
    | 'label'
    | 'extra'
    | 'light'
    | 'height'
    | 'condensed'
    | 'first'
    | 'last'
    | 'selected'
    | 'style'
    | 'onClick'
    | 'onDoubleClick'
    | 'onRightClick'
    | 'onMouseOver'
    | 'onMouseEnter'
    | 'onMouseLeave'
    | '_ref']?: any;
} & {
  indicators?: ReadonlyArray<unknown>;
};

const ListHeader = ({
  icon,
  label,
  indicators = emptyArray,
  extra,
  light,
  height,
  condensed,
  first,
  last,
  selected,
  style,
  onClick,
  onDoubleClick,
  onRightClick,
  onMouseOver,
  onMouseEnter,
  onMouseLeave,
  _ref,
}: ListHeaderProps) => (
  <Wrapper
    style={style}
    selected={selected}
    first={first}
    last={last}
    light={light}
    condensed={condensed}
    height={height}
    onContextMenu={onRightClick}
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    onMouseOver={onMouseOver}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    _ref={_ref}
  >
    <NoEmpty>{icon}</NoEmpty>
    <FlexRow grow={1} align="center">
      {label}

      <NoEmpty>{indicators}</NoEmpty>
    </FlexRow>
    <NoEmpty>{extra}</NoEmpty>
  </Wrapper>
);

const ListItem = styled(ListHeader)(
  ({theme: {gutters, control, colors}, first, last}) => {
    const firstRadius = first ? control.borderRadius : 0;
    const thirdRadius = last ? control.borderRadius : 0;
    const fourthRadius = last ? control.borderRadius : 0;
    return {
      flexShrink: 0,
      borderRadius: `${firstRadius}px 0px ${thirdRadius}px ${fourthRadius}px`,
      borderColor: colors.borderDeep,
      padding: gutters.x8,
      marginTop: -1,
    };
  },
  {
    hover: ({theme: {colors}}) => ({
      background: colors.backgroundDeep,
    }),
  }
);

const tooltipContent = (content: string) => (
  <ContentWrapper>{content}</ContentWrapper>
);

// @ts-expect-error - TS7031 - Binding element 'onEditClick' implicitly has an 'any' type. | TS7031 - Binding element 'dataAutomationId' implicitly has an 'any' type.
const EditIcon = ({onEditClick, 'data-automation-id': dataAutomationId}) => (
  <FlexRow align="center" justify="center">
    <TooltipTrigger
      alternate
      placement="top"
      content={tooltipContent('Edit error message')}
      copy
    >
      {/* TODO: fix jsx-a11y lint error */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions */}
      <a onClick={onEditClick} data-automation-id={dataAutomationId}>
        <PencilIcon />
      </a>
    </TooltipTrigger>
  </FlexRow>
);

type ErrorItemProps = {
  label: any;
  icon: any;
  first: boolean;
  last: boolean;
  selected: boolean;
  itemHover: boolean;
  onClick: () => void;
  onEditClick: (itemId: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  ['data-automation-id']: string;
  _ref: React.Ref<HTMLDivElement>;
};

export const ErrorItem = ({
  label,
  icon,
  first,
  last,
  selected,
  itemHover,
  onClick,
  onEditClick,
  onMouseEnter,
  onMouseLeave,
  'data-automation-id': dataAutomationId,
  _ref,
}: ErrorItemProps) => (
  <Hitarea>
    <ListItem
      _ref={_ref}
      icon={icon}
      label={label}
      extra={
        itemHover ? (
          <EditIcon
            onEditClick={onEditClick}
            data-automation-id={dataAutomationId}
          />
        ) : null
      }
      onClick={onClick}
      first={first}
      last={last}
      selected={selected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  </Hitarea>
);
